'use client'
import React from 'react'
import { useTranslations } from '@/lib/next-shim'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { LoadingButton } from '@/components/external/material'
import { Link } from '@/lib/next-shim'
import DateInput from '@/components/ui/forms/DateInput'
import SelectInput from '@/components/ui/forms/SelectInput'
import { Gender } from '@/core/graphql/types'
import { useGetEstablishments } from '@/shared/model/useGetEstablishments'

import { ZoomDocNYRegisterFormData, useZoomDocNYRegisterForm } from '../model/zoomDocNYRegisterForm'
import { SubmitHandler } from 'react-hook-form'

type ZoomDocNYRegisterFormProps = {
  isLoading?: boolean
  onSubmit: SubmitHandler<ZoomDocNYRegisterFormData>
}

export function ZoomDocNYRegisterForm({ isLoading = false, onSubmit }: ZoomDocNYRegisterFormProps) {
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useZoomDocNYRegisterForm()
  const t = useTranslations()
  const { establishments } = useGetEstablishments()

  return (
    <form className="flex flex-col space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Basic Information */}
      <div>
        <TextInputController control={control} fullWidth label={t('field.email.label')} name="email" required />
      </div>
      <div>
        <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" required />
      </div>
      <div>
        <TextInputController control={control} fullWidth label={t('field.surname.label')} name="surname" required />
      </div>
      <div>
        <SelectInput<ZoomDocNYRegisterFormData>
          id="establishment"
          label={t('field.establishment.label')}
          colSpan="col-span-6"
          options={[
            ...establishments.map((est) => ({
              value: est._id,
              label: est.name,
            })),
          ]}
          name="establishment"
          control={control}
        />
      </div>
      <div className="flex space-x-4">
        <div className="flex-1">
          <TextInputController control={control} fullWidth label={t('field.phone.label')} name="phone" />
        </div>
        <div className="flex-1">
          <TextInputController control={control} fullWidth label={t('field.secu.label')} name="secu" />
        </div>
      </div>
      <div className="flex space-x-4">
        <div className="flex-1">
          <DateInput<ZoomDocNYRegisterFormData> id="birthday" label={t('field.birthday.label')} control={control} colSpan="col-span-6" required />
        </div>
        <div className="flex-1">
          <SelectInput<ZoomDocNYRegisterFormData>
            id="gender"
            label={t('field.gender.label')}
            colSpan="col-span-6"
            options={Object.values(Gender).map((elem) => ({
              value: elem,
              label: elem,
            }))}
            name="gender"
            control={control}
            required
          />
        </div>
      </div>

      {/* Insurance Information Section */}
      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('page.AccountSettings.profile.insuranceInfo')}</h3>
        <div className="space-y-4">
          <div>
            <TextInputController control={control} fullWidth label={t('field.insured.label')} name="insured" />
          </div>
          <div>
            <TextInputController control={control} fullWidth label={t('field.insuredRelationship.label')} name="insuredRelationship" />
          </div>
          <div>
            <TextInputController control={control} fullWidth label={t('field.insuranceProvider.label')} name="insuranceProvider" />
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <TextInputController control={control} fullWidth label={t('field.primaryInsurance.label')} name="primaryInsurance" />
            </div>
            <div className="flex-1">
              <TextInputController control={control} fullWidth label={t('field.primaryPolicy.label')} name="primaryPolicy" />
            </div>
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <TextInputController control={control} fullWidth label={t('field.secondaryInsurance.label')} name="secondaryInsurance" />
            </div>
            <div className="flex-1">
              <TextInputController control={control} fullWidth label={t('field.secondaryPolicy.label')} name="secondaryPolicy" />
            </div>
          </div>
          <div>
            <TextInputController control={control} fullWidth label={t('field.primaryPharmacy.label')} name="primaryPharmacy" />
          </div>
        </div>
      </div>

      {/* Password Section */}
      <div className="pt-4 border-t border-gray-200">
        <div className="space-y-4">
          <div>
            <TextInputController control={control} fullWidth label={t('field.password.label')} name="password" TextInputProps={{ type: 'password' }} required />
          </div>
          <div>
            <TextInputController control={control} fullWidth label={t('field.confirmPassword.label')} name="confirmPassword" TextInputProps={{ type: 'password' }} required />
          </div>
        </div>
      </div>

      {/* Terms and Submit */}
      <div className="flex items-start">
        <CheckboxInputController
          control={control}
          label={
            <div className="font-light text-gray-500">
              {t.rich('page.Register.signUpForm.field.termsAndConditions.label', {
                link: (chunks: React.ReactNode) => (
                  <Link className="font-medium text-primary-600 hover:underline" href="/terms-and-conditions" target="_blank">
                    {chunks}
                  </Link>
                ),
              })}
            </div>
          }
          name="terms"
        />
      </div>
      <LoadingButton
        className="w-full py-5 font-medium rounded-lg text-sm"
        disabled={!isValid}
        disableElevation
        loading={isLoading}
        type="submit"
        variant="contained"
      >
        {t('button.signUp.label')}
      </LoadingButton>
    </form>
  )
}
